<template>
    <div>
        <v-img
                v-for="uuid in files"
                :key="uuid"
                :src="getIconThumbnail(uuid)"
                width="160px" />
        <v-btn
                v-if="!!uuid"
                class="mx-2"
                fab
                dark
                small
                color="error"
                @click="clearIcon">
            <v-icon dark>
                mdi-delete
            </v-icon>
        </v-btn>
        <input
                :id="uploaderId"
                type="hidden"
                role="uploadcare-uploader"
                :data-public-key="uploadcareKey"
                :data-multiple="multiple"
                :data-tabs="tabs">
        <svg
                width="0"
                height="0"
                style="position:absolute">
            <symbol
                    :id="'uploadcare--icon-' + webcamTabId"
                    viewBox="0 0 32 32">
                <path d="M21 10h3c1.653 0 3 1.343 3 3v9c0 1.656-1.344 3-3.001 3H8A3 3 0 0 1 5 22v-9a3 3 0 0 1 3-3h3v-.999C11 7.901 11.895 7 13 7h6c1.113 0 2 .896 2 2.001V10zm-5 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9zm0-2a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
            </symbol>
        </svg>

        <svg
                width="0"
                height="0"
                style="position:absolute">
            <symbol
                    id="uploadcare--icon-gallery"
                    viewBox="0 0 32 32">
                <path d="M 16 22.928 L 23.416 27.4 L 21.454 18.965 L 28 13.292 L 19.37 12.552 L 16 4.6 L 12.629 12.552 L 4 13.292 L 10.546 18.965 L 8.584 27.4 Z" />
            </symbol>
        </svg>

        <WebcamDialog
                :webcam-id="fieldId"
                :dialog="webcamOpen"
                :stencil="stencil"
                @close-dialog="webcamOpen = false"
                @open-dialog="webcamOpen = true"
                @photo-saved="saveObject" />

        <v-speed-dial
                v-if="openedDialog"
                v-model="uploadSource"
                bottom
                right
                direction="top">
            <template v-slot:activator>
                <v-btn
                        v-model="uploadSource"
                        class="uploader-mobile-switcher"
                        color="blue darken-2"
                        dark
                        fab>
                    <v-icon v-if="uploadSource">
                        mdi-close
                    </v-icon>
                    <v-icon v-else>
                        mdi-upload
                    </v-icon>
                </v-btn>
            </template>
            <v-btn
                    fab
                    dark
                    small
                    color="green">
                <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
                    fab
                    dark
                    small
                    color="indigo">
                <v-icon>mdi-plus</v-icon>
            </v-btn>
            <v-btn
                    fab
                    dark
                    small
                    color="red">
                <v-icon>mdi-delete</v-icon>
            </v-btn>
        </v-speed-dial>
    </div>
</template>

<script>
    import uploadcare from 'uploadcare-widget'
    import WebcamDialog from "./WebcamDialog";
    import {UPLOAD_PREVIEW_ENDPOINT} from "@/models/application.model";
    import {TYPE_FILE_IMAGE, TYPE_FILE_DOCUMENT} from "@/models/field.model";

    export default {
        name: "FileUploader",
        components: {WebcamDialog},
        model: {
            prop: 'uuid',
            event: 'upload'
        },
        props: {
            fieldId: {type: String, default: ''},
            uuid: {type: String, default: ''},
            stencil: {type: String, default: ''},
            multiple: {type: Boolean, default: false},
            gallery: {type: Array, default: () => []},
            type: {type: String, default: TYPE_FILE_IMAGE}
        },
        data() {
            return {
                webcamOpen: false,
                webcamDialogApi: null,
                uploaded: null,
                uploadCancelled: false,
                openedDialog: false,
                dialog: null,
                uploadSource: false
            }
        },
        computed: {
            isDocument() {
                return this.type === TYPE_FILE_DOCUMENT;
            },
            galleryVisible() {
                return !this.isDocument && this.gallery && this.gallery.length > 0;
            },
            webCamVisible() {
                return !this.isDocument;
            },
            webcamBtnSelector() {
                return '#'  + this.webcamBtnId
            },
            uploaderSelector() {
                return '#' + this.uploaderId + '[role=uploadcare-uploader]'
            },
            webcamTabId() {
                return 'webcam-tab-' + this.fieldId
            },
            webcamBtnId() {
                return 'webcam-btn-show-' + this.fieldId
            },
            uploaderId() {
                return 'uploader-'+this.fieldId
            },
            files() {
                let isJson = false;

                try {
                    JSON.parse(this.uuid);
                    isJson = true;
                } catch (e) {
                    //
                }

                if (isJson) {
                    return JSON.parse(this.uuid)
                } else {
                    return [this.uuid]
                }
            },
            uploadcareKey() {
                return process.env.VUE_APP_UPLOADCARE_KEY;
            },
            tabs() {
                let tabs = 'file url';
                if (this.webCamVisible) {
                    tabs = this.stencil
                        ? this.webcamTabId + ' ' + tabs
                        : 'camera ' + tabs
                }
                if (this.galleryVisible) {
                    tabs = 'gallery ' + tabs
                }
                return tabs
            },
            sessionKey() {
                return this.$store.forms.getSessionKey();
            },
            sessionToken() {
                return this.$store.forms.getSessionToken();
            }
        },
        watch: {
            uploaded() {
                if (Array.isArray(this.uploaded)) {
                    this.$emit('upload', JSON.stringify(this.uploaded));
                } else {
                    this.$emit('upload', this.uploaded);
                }
            }
        },
        mounted() {
            if (this.webCamVisible) {
                uploadcare.registerTab(this.webcamTabId, (container, button, dialogApi) => {
                    this.webcamDialogApi = dialogApi;
                    container
                        .append('<button id="' + this.webcamBtnId + '" class="webcam-show-btn uploadcare--button uploadcare--button_size_big uploadcare--button_primary">Take photo</button>')
                        .on('click', this.webcamBtnSelector, () => {
                            this.showWebcam()
                        })
                });
            }

            if (this.galleryVisible) {
                uploadcare.registerTab('gallery', (container, button, dialogApi, settings) => {
                    this.gallery.forEach((galleryItem) => {
                        container.append(
                            '<img class="gallery-item" data-uuid="'+galleryItem.id+'" src="'+galleryItem.url+'">'
                        ).on('click', '.gallery-item[data-uuid='+galleryItem.id+']', function () {
                            dialogApi.addFiles([uploadcare.fileFrom('uploaded', galleryItem.id, settings)])
                        })
                    });
                })
            }

            const widget = uploadcare.Widget(this.uploaderSelector, {
                previewUrlCallback: this.previewUrlCallback,
                metadata: {
                    session_key: this.sessionKey,
                    session_token: this.sessionToken
                }
            });

            let isJson = false;
            const self = this;

            try {
                JSON.parse(this.uuid);
                isJson = true;
            } catch (e) {
                //
            }

            if (this.uuid) {
                if (this.multiple && isJson) {
                    widget.value(JSON.parse(this.uuid))
                } else {
                    widget.value(this.uuid)
                }
            }

            if (this.type === TYPE_FILE_IMAGE) {
                widget.validators.push(function imagesOnly(fileInfo) {
                    if (fileInfo.isImage === false) {
                        self.$services.modals.alerts.error('Only images are allowed for this field. Please upload an image instead.')
                        self.$emit('loading', false)
                        throw new Error('image');
                    }
                })
            }

            if (this.webCamVisible) {
                widget.onDialogOpen((dialog) => {
                    dialog.dialogElement
                        .find('.uploadcare--camera')
                        .append(
                            '<button ' +
                                'style="max-width: 800px; margin: 0 auto;" ' +
                                'class="uploadcare-switch-to-file uploadcare--button uploadcare--button_size_small uploadcare--button_info">' +
                                'Upload local file' +
                                '</button>'
                        );

                    dialog.dialogElement
                        .find('.uploadcare-switch-to-file')
                        .click(function () {
                            dialog.switchTab('file')
                        })


                    dialog.dialogElement
                        .find('.uploadcare--draganddrop')
                        .append(
                            '<button ' +
                                'style="max-width: 800px; margin: 0 auto; margin-top: 10px;" ' +
                                'class="uploadcare-switch-to-camera uploadcare--button uploadcare--button_size_small uploadcare--button_info">' +
                                'Capture from camera' +
                                '</button>'
                        );

                    dialog.dialogElement
                        .find('.uploadcare-switch-to-camera')
                        .click(function () {
                            dialog.switchTab('camera')
                        })
                });
            }

            widget.onChange((file) => {
                if (file) {
                    self.$emit('loading', true)
                } else {
                    self.$emit('loading', false)
                }
            })

            widget.onUploadComplete((info) => {
                if (self.uploadCancelled) {
                    self.$emit('loading', false)
                    return;
                }
                if (self.multiple) {
                    self.uploaded = [];
                    uploadcare.loadFileGroup(info.uuid, {debugUploads: true, publicKey: self.uploadcareKey})
                        .done((fileGroup) => {
                            fileGroup.files().forEach((file) => {
                                file.done((fileInfo) => {
                                    self.uploaded.push(fileInfo.uuid);
                                })
                            })
                        })
                } else {
                    self.uploaded = info.uuid;
                }
                // uploaded all
                self.$emit('loading', false)
            });
        },
        beforeDestroy() {
            // delete component from the page
            this.$emit('loading', false)
        },
        methods: {
            previewUrl() {
                return process.env.VUE_APP_DATA_CAPTURE_API
                    .replace(
                        'ROOT_DOMAIN',
                        (process.env.VUE_APP_ROOT_DOMAIN || window.location.hostname)
                            .split('.')
                            .slice(-2)
                            .join('.')
                    )
                    + UPLOAD_PREVIEW_ENDPOINT;
            },
            previewUrlCallback(previewUrl, fileInfo) {
                return this.previewUrl() +
                    `uuid=${fileInfo.uuid}&` +
                    `upload_session=${this.sessionKey}&` +
                    `upload_session_token=${this.sessionToken}`
            },
            saveObject: function(file) {
                let arr = file.split(','),
                    mime = arr[0].match(/:(.*?);/)[1],
                    bstr = atob(arr[1]),
                    n = bstr.length,
                    u8arr = new Uint8Array(n);
                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                this.webcamDialogApi.addFiles([uploadcare.fileFrom(
                    'object',
                    new File([u8arr], 'camera.jpg', {type:mime}),
                    {
                        publicKey: process.env.VUE_APP_UPLOADCARE_KEY,
                        previewUrlCallback: this.previewUrlCallback,
                        metadata: {
                            session_key: this.sessionKey,
                            session_token: this.sessionToken
                        }
                    }
                )]);
            },
            showWebcam: function () {
                this.webcamOpen = true
            },
            getIconThumbnail(uuid) {
                return this.previewUrlCallback(null, {uuid: uuid})
            },
            clearIcon() {
                const widget = uploadcare.Widget(this.uploaderSelector);
                widget.value(null);
                this.$emit('upload', null);
            }
        },
    }
</script>

<style>
	.webcam-show-btn {
		width: initial !important;
		margin: 0 auto !important;
	}
	.uploadcare--dialog {
		z-index: 200 !important;
	}

	.gallery-item {
		width: 140px;
		height: 140px;
		margin: 10px;
		padding: 5px;
		color: rgba(0, 0, 0, 0.4);
		box-shadow: 0px 3px 10px -3px;
	}
	.gallery-item:hover {
		cursor: pointer;
		box-shadow: 0px 6px 20px -3px;
	}
	.uploadcare--tab_name_gallery {
		flex-wrap: wrap;
		overflow: scroll;
		flex-direction: row;
		justify-content: left;
		flex-grow: 0;
	}
	.uploadcare--menu__item_tab_camera::after {
		content: 'Camera' !important;
	}
	.uploadcare--menu__item_tab_url::after {
		content: 'Direct link' !important;
	}
	.uploadcare--menu__item_tab_file::after {
		content: 'Local files' !important;
	}
	.uploadcare--menu__item_tab_gallery::after {
		content: 'Your gallery' !important;
	}

  #uploader-styling {
    margin-top: 10px;
  }
  #uploader-styling .uploadcare--panel,
  #uploader-styling .uploadcare--menu__item,
  #uploader-styling .uploadcare--menu__items {
    background-color: transparent;
    border: 0;
  }
  #uploader-styling .uploadcare--panel {
    flex-direction: column;
  }
  #uploader-styling .uploadcare--menu {
    width: 100%;
    height: 60px;
    min-height: 60px;
  }
  #uploader-styling .uploadcare--menu__items {
    padding-right: 0;
    flex-direction: row;
  }
  .uploader-mobile-switcher {

  }
</style>